<template>
    <div class="tw-flex tw-mt-4 tw-justify-center md:tw-justify-start tw-items-center">
        <!-- View product -->
        <Link v-if="(!course.availability.available || !is_released) && !extended" :to="toUrl">
            <div class="cta-button">{{ $t('general.view') }} {{ $t('membership.product').toLowerCase() }}</div>
        </Link>

        <!-- Buy product -->
        <Link v-else-if="!course.availability.available && course.salespage" :to="toUrl" target="_blank">
            <div class="cta-button">{{ $t('membership.buy_product') }}</div>
        </Link>

        <!-- Start product -->
        <Link v-else-if="course.availability.available && is_released  && course.progress === 0" :to="toUrl">
            <div class="cta-button">Start {{ $t('membership.product').toLowerCase() }}</div>
        </Link>

        <!-- Start again -->
        <Link v-else-if="course.availability.available && is_released && course.progress === 100" :to="toUrl">
            <div class="cta-button">{{ $t('membership.start_again') }}</div>
        </Link>

        <!-- Continue -->
        <Link v-else-if="course.availability.available && is_released && (course.progress > 0 && course.progress < 100)"
              :to="{name: 'lessons.show', params: { course_slug: course.slug, module_slug: course.last_activity.module.slug, lesson_slug: course.last_activity.slug }}"
        >
            <div class="cta-button">{{ $t('membership.continue_progress_short') }}</div>
        </Link>

        <!-- Access soon-->
        <Link v-else-if="access_to_course_from" :to="toUrl">
            <div class="cta-button">{{ $t('membership.access_soon') }}</div>
        </Link>

        <p class="tw-flex tw-items-center tw-font-bold tw-mb-8 md:tw-mb-0 md:tw-ml-4" v-if="access_to_course_until && is_released">
            <i :class="['fas tw-mt-2px tw-mr-2', access_to_course_until.below_seven_days_remaning ? 'fa-exclamation-triangle' : 'fa-clock']"></i>
            {{ access_to_course_until.text }}
        </p>
        
        <p class="tw-flex tw-items-center tw-font-bold tw-mb-8 md:tw-mb-0 md:tw-ml-4" v-else-if="access_to_course_from && is_released">
            <i class="fas tw-mt-2px tw-mr-2 fa-clock"></i>
            {{ access_to_course_from }}
        </p>

        <!-- CTA supportive text-->
        <template v-if="!is_released && course.release_date !== null">
            <div class="tw-relative">
                <clickable-icon v-if="$user" v-tooltip.bottom="$t('membership.set_reminder_explanation')" bg_color="very-light" icon_animation="rotate-10-deg" class="tw-mr-2" @click.native="setReminderForReleaseDate">
                    <div class="tw-relative bell-check" :class="'theme-' + course.color_theme">
                        <i class="fa fa-lg fa-bell bell"></i>
                        <i v-if="reminders.length >= 1" class="fa fa-check overlapping-icon"></i>
                    </div>
                </clickable-icon>
            </div>

            <div class="tw-flex tw-justify-center tw-items-center">
                <div class="tw-flex tw-flex-col tw-opacity-75 tw-text-white">
                    <div class="tw-font-bold tw-text-xs tw-text-left">
                        <span class="tw-whitespace-nowrap">{{ $t('membership.soon_available').toUpperCase() }}</span>
                        -
                        <span class="tw-whitespace-nowrap">{{ formatted_release_date }}</span>
                    </div>
                    <countdown :deadline="course.release_date" variant="string" @countdownFinished="refreshPage()" class="tw-hidden md:tw-block"></countdown>
                </div>
            </div>
        </template>
    </div>
</template>

<script>
    import Link from "../Link.vue";
    import Countdown from "../ui/Countdown.vue";
    import Modal from "../../mixins/v2/Modal";
    import Course from "../../mixins/v2/Course";
    import DateMixin from "../../mixins/Date";
    import Reminder from '@/resources/assets/js/vue/lib/models/Reminder';
    import ReminderModal from "../ui/ReminderModal";
    import ReminderDropdown from "../ui/ReminderDropdown";
    import { EventBus } from "../../lib/event-bus";
    import Notification from "../../lib/models/Notification";

    export default {
        name: 'CourseHeroCTA',

        mixins: [Modal, DateMixin, Course],

        props: ['course', 'extended'],

        components: {
            Link,
            'countdown': Countdown,
            'reminder-modal': ReminderModal,
            'reminder-dropdown': ReminderDropdown,
        },

        data() {
            return {
                reminders: [],
            }
        },

        computed: {
	        is_released() {
		        if (this.course.release_date === null) {
			        return false;
		        }

		        return Date.now() >= new Date(this.browserSafeDate(this.course.release_date));
	        },

            formatted_release_date() {
                return moment(this.course.release_date).format('DD-MM-YY');
            },

            toUrl() {
                if (!this.course.availability.available) {
                    if (this.access_to_course_from) {
                        return '#';
                    }

                    if (this.extended) {
                        if (!this.user && this.course.salespage === '') {
                            return '/auth/login#login';
                        }

                        return this.course.salespage;
                    }

                    return {
                        name: 'courses.show',
                        params: {
                            course_slug: this.course.slug,
                        },
                    };
                }

				if (!this.is_released) {
					return {
						name: 'courses.show',
						params: {
							course_slug: this.course.slug,
						},
					};
				}

                if (this.course.first_lesson_url === '#') {
                        return '#';
                }

                const regex = '\/resources\\/(.*\\/)(.*\\/)(.*)';
                const match = this.course.first_lesson_url.match(regex);

                return {
                    name: 'lessons.show',
                    params: {
                        course_slug: match[1].slice(0, -1),
                        module_slug: match[2].slice(0, -1),
                        lesson_slug: match[3],
                    },
                };
            },
        },

        methods: {
            refreshPage() {
                this.$router.go();
            },

            setReminderForReleaseDate() {
                const release_date_reminder = {
                    duration: 1,
                    unit: 'minutes',
                    value: true,
                };

                if(this.reminders.length >= 1) {
                    Reminder.api().deleteAllReminders(this.course.id, 'product').then(() => {
                        this.getReminders();
                        if(!this.extended) {
                            EventBus.$emit('reminderChanged', this.course.id);
                        }
                        Notification.insert({
                            data: [
                                {
                                    title: this.$t('membership.removed_reminder_notification'),
                                },
                            ]
                        });
                    })
                } else {
                    Reminder.api().store(this.course.id, 'product', [release_date_reminder]).then(() => {
                        this.getReminders();
                        if(!this.extended) {
                            EventBus.$emit('reminderChanged', this.course.id);
                        }
                        Notification.insert({
                            data: [
                                {
                                    title: this.$t('membership.added_reminder_notification'),
                                },
                            ]
                        });
                    });
                }
            },

            getReminders(){
                Reminder.api().fetchAllReminders(this.course.id, 'product').then((response) => {
                    this.reminders = response.response.data.data;
                });
            }
        },

        created() {
            this.getReminders();

            if(!this.extended){
                EventBus.$on('reminderChanged', (course_id) => {
                    if(this.course.id === course_id) {
                        Reminder.api().fetchAllReminders(this.course.id, 'product').then((response) => {
                            this.reminders = response.response.data.data;
                        })
                    }
                })
            }
        }
    };
</script>

<style lang="scss" scoped>
    .cta-button {
	    @apply tw-mr-4 tw-inline-block tw-bg-white tw-select-none tw-text-center;
        padding: 14px;
        border-radius: 100px;
        padding-left: 50px;
        padding-right: 50px;
        font-weight: 600;
        font-size: 14px;
        color: #636363;
        box-shadow: 0 6px 15px 0 rgba(36, 37, 38, .08);
        transition: all .3s ease;

        &:hover {
            box-shadow: 0 6px 15px 5px rgba(36, 37, 38, 0.12);
            transform: translateY(-2px);
        }

        &:active {
            opacity: 0.8;
        }
    }

    .text-color {
        color: #37474F;
    }

    .flex-grow {
        flex-grow: 1;
    }

    .secondary-button {
        font-family: Quicksand;
        color: #37474F;
    }

    .bell {
        &:hover, &:focus, &:active {
            animation: shake .5s cubic-bezier(.35,.08,.18,.96) both;
        }
    }

    @keyframes shake {
        0% { transform: rotate(0); }
        15% { transform: rotate(5deg); }
        30% { transform: rotate(-5deg); }
        45% { transform: rotate(4deg); }
        60% { transform: rotate(-4deg); }
        75% { transform: rotate(2deg); }
        85% { transform: rotate(-2deg); }
        92% { transform: rotate(1deg); }
        100% { transform: rotate(0); }
    }

    .bell-check {
        @mixin hero-theme($light_color, $color, $dark_color, $extra_light_color) {
            .overlapping-icon {
                position: absolute;
                font-size: 9px;
                left: 50%;
                top: 50%;
                transform: translate(-50%, -52%);
                color: $color;
            }
        }

        &.theme-teal {
            @include hero-theme(#2ebda9, #27a291, #1c7d70, #2ec3af);
        }

        &.theme-pink {
            @include hero-theme(#ff4c89, #E91E63, #bf154f, #ff5b93);
        }

        &.theme-orange {
            @include hero-theme(#ffb74c, #FF9800, #da8303, #ffc166);
        }

        &.theme-blue {
            @include hero-theme(#65b9fd, #2196F4, #1a81d4, #57b4ff);
        }

        &.theme-light_blue {
            @include hero-theme(#81D4FA, #4FC3F7, #039BE5, #B3E5FC);
        }

        &.theme-red {
            @include hero-theme(#EF5350, #E53935, #C62828, #E57373);
        }

        &.theme-purple {
            @include hero-theme(#BA68C8, #9C27B0, #7B1FA2, #CE93D8);
        }

        &.theme-deep_purple {
            @include hero-theme(#9575CD, #673AB7, #512DA8, #B39DDB);
        }

        &.theme-indigo {
            @include hero-theme(#7986CB, #3F51B5, #303F9F, #C5CAE9);
        }

        &.theme-cyan {
            @include hero-theme(#00BCD4, #0097A7, #00838F, #4DD0E1);
        }

        &.theme-light_green {
            @include hero-theme(#DCEDC8, #AED581, #689F38, #F1F8E9);
        }

        &.theme-green {
            @include hero-theme(#4CAF50, #388E3C, #7fad4d, #81C784);
        }

        &.theme-light_orange {
            @include hero-theme(#E89A82, #EF7956, #D06748, #E6AA97);
        }

        &.theme-deep_orange {
            @include hero-theme(#FF8A65, #FF5722, #D84315, #FFAB91);
        }

        &.theme-gray {
            @include hero-theme(#616161, #212121, #1d1d1d, #757575);
        }

        &.theme-light_gray {
            @include hero-theme(#e3e4e6, #d3d4d8, #97989c, #eff1f5);
        }

        &.theme-metallic {
            @include hero-theme(#90A4AE, #607D8B, #455A64, #CFD8DC);
        }

        &.theme-brown {
            @include hero-theme(#A1887F, #795548, #5D4037, #D7CCC8);
        }
    }

</style>
